// apps/landing-page/src/components/search/useUrlQuery.ts
// A search box whose value lives in the URL (?q=), written after a pause so typing does
// not hammer the router. The URL is the source of truth: back/forward update the box.
'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

const DEBOUNCE_MS = 200;

export function useUrlQuery() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const q = searchParams.get('q') ?? '';

  // `input` is what is being typed; `q` catches up after a pause. lastWritten tells our own
  // URL write apart from a back/forward change, so a late write never overwrites keystrokes
  // typed since.
  const [input, setInput] = useState(q);
  const lastWritten = useRef(q);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onChange = (value: string) => {
    setInput(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const next = value.trim();
      lastWritten.current = next;
      // Read the live URL: other filters may have changed since this keystroke.
      const params = new URLSearchParams(window.location.search);
      if (next) params.set('q', next); else params.delete('q');
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }, DEBOUNCE_MS);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (q !== lastWritten.current) {
      lastWritten.current = q;
      setInput(q);
    }
  }, [q]);

  return { q, input, onChange };
}
